/**
 * Strings for the review desk (src/components/admin/*.vue).
 *
 * All three languages live in this one module, nested under the single
 * top-level group `admin`, so the desk can be translated without four agents
 * editing the same three files. src/main.js merges it.
 *
 * TWO RULES THIS FILE HAS TO KEEP, both of them properties of vue-i18n 8 rather
 * than matters of taste:
 *
 *   1. src/main.js sets `fallbackLocale: 'en-US'`. So a key present in English
 *      and missing in Chinese does NOT render as its own key path — it renders
 *      the English string, quietly, in the middle of a Chinese page. That is the
 *      harder failure to notice, not the easier one, which is why the three
 *      trees must hold exactly the same keys: the symmetry below is maintained
 *      by hand and worth checking when adding one. (An earlier version of this
 *      header claimed the opposite and told the next translator to expect a
 *      visible "admin.desk.approve" on a button.)
 *
 *   2. Message text is compiled, not printed. Curly braces open an interpolation
 *      slot, a pipe is the plural separator on the $tc path, and an at-sign
 *      immediately followed by a colon is a LINK that splices another message in
 *      here. So the ASCII forms of those three characters appear below only
 *      where a parameter is genuinely meant; where the text has to talk ABOUT
 *      them it says so in words or shows the fullwidth look-alike (＠：), which
 *      is inert. This is the same constraint lib/text-safety.mjs enforces on
 *      submitted captions, and this file is not exempt from it.
 *
 * REGISTER. The site talks to fans and is warm about it. This screen talks to
 * one person doing a job, so it stays warm but gets to the point, and it never
 * gets cute about the two things that can bite: that approving is not
 * publishing, and that the approve button is disabled on purpose. The Japanese
 * is written as a Japanese speaker would write it, not as a rendering of the
 * Chinese.
 */
export default {
  'zh-CN': {
    admin: {
      admins: {
        title: '管理员',
        subtitle: '谁能进这个后台。种子管理员来自部署配置，删不掉；其余是在线邀请来的。',
        refresh: '刷新',
        you: '你',
        seed: '种子',
        invited: '受邀',
        seedNote: '来自部署配置，不可在此移除',
        remove: '移除',
        removing: '移除中…',
        removeConfirm: '确定移除管理员 {id}？他会立刻失去后台权限。',
        inviteTitle: '邀请新管理员',
        inviteLead: '生成一句话，让对方到本站直播间当普通弹幕发出去；对方发出后，你再确认。',
        startInvite: '生成邀请',
        opening: '正在接直播间…',
        postThis: '让对方把下面这句话发到直播间：',
        openRoom: '打开直播间',
        copied: '已复制',
        connecting: '正在连直播间，稍等…',
        expiresIn: '剩余 {s} 秒',
        claimedBy: '这句话被下面这个身份认领了：',
        confirmWarn: '确认前请核对：这是不是你要加的那个人？确认后对方立即成为管理员。',
        confirm: '确认，设为管理员',
        confirming: '正在确认…',
        confirmedNow: '{id} 现在是管理员了。',
        expired: '这句话已过期，重新生成一个。',
        done: '完成',
        cancel: '取消'
      },
      nav: {
        title: '审核台',
        twoStage: '通过 ≠ 上线，发布是另外一步',
        queue: '队列',
        publish: '发布',
        clips: '音声库',
        recycle: '回收站',
        audit: '记录',
        storage: '存储',
        theme: '主题',
        branding: '品牌',
        admins: '管理员'
      },
      common: {
        refresh: '刷新',
        retry: '再试一次',
        loading: '读取中…'
      },
      error: {
        unreachable: '连不上后台。可能是网断了，也可能是服务没起来 —— 这不代表你被登出了。'
      },
      queue: {
        title: '待审队列',
        order: '按送来的先后排，没有别的排序。谁先来谁先看。',
        counts: '{batches} 批 · {items} 条',
        truncated: '只显示最早的 {limit} 条，后面还有。',
        empty: '空了，暂时没有要审的。',
        submittedAt: '送来于',
        history: '过往：通过 {approved} · 退回 {rejected} · 投过 {submitted} 次',
        drift: '这位的计数和实际对不上。实际是：通过 {approved} · 退回 {rejected} · 投过 {submitted}。没人会自动修 —— 自动修就把出错的原因一起盖掉了。',
        blocked: '这位投稿人被标记为 blocked。那是投稿那边的限制，不影响你在这里怎么定。',
        sourceWeb: '来源：网页投稿',
        sourceApi: '来源：API · 自称「{label}」',
        sourceUnnamed: '未命名客户端',
        colPosition: '序',
        colName: '投稿人写的名字',
        colGroup: '想放的分类',
        colClip: '音频',
        colNote: '留言',
        colAction: '操作',
        clipSize: '{seconds} 秒 · {kb} KB · {ext}',
        noNote: '没留言',
        noGroup: '没指定',
        proposedGroup: '想新建：{name}',
        review: '去审',
        open: '正在审',
        heard: '已听完'
      },
      audition: {
        title: '先听一遍',
        length: '{seconds} 秒',
        play: '播放',
        restart: '从头再放',
        pause: '暂停',
        resume: '接着放',
        volume: '音量',
        statusIdle: '还没听',
        statusPlaying: '播放中 · {seconds} 秒',
        statusPaused: '停在 {seconds} 秒',
        statusHeard: '整条听完了',
        statusStalled: '播放器还没说它放完',
        stalledHint: '可能只是在缓冲，也可能这个文件根本取不到、或者浏览器放不了。再放一次试试；要是一直这样，先别通过它。',
        statusTruncated: '还没放到底就停了',
        truncatedHint: '只放了 {played} 秒，收下时记的是 {expected} 秒。卷上的这个文件跟当初收下的那个对不上了，别通过它——先去查这条音声的文件。',
        gate: '整条放完，通过按钮才会亮。上面那条是按时间估的进度；真正算数的，是播放器自己报的“放完了”。中间暂停没关系，接着放就行 —— 这里没有拖动条，所以只能一秒一秒听过去。'
      },
      desk: {
        title: '这一条',
        pick: '从队列里挑一条开始。',
        backToQueue: '← 回队列',
        backToRecycle: '← 回回收站',
        position: '第 {position} 条 · 这批共 {count} 条',
        batchProgress: '这一批：待审 {pending} · 通过 {approved} · 退回 {rejected}',
        proposedName: '投稿人写的名字',
        proposedGroup: '投稿人想放的分类',
        submitterCaption: '投稿人写的字幕',
        submitterNote: '投稿人的话',
        submitterSource: '投稿人提供的来源',
        alreadyDecided: '这条已经是 {state} 了（{at}）。',
        notLiveYet: '已通过，但还没上线 —— 站上现在还看不到它。',
        live: '已上线（{at}）。',
        publishBlocked: '少了这些语言的文案，发布会被拒：{locales}',
        goPublish: '去发布 →',
        draftRestored: '把你上次没写完的草稿恢复了（{at}）。',
        draftDiscard: '丢掉草稿，回到投稿原样',
        draftStorageFailed: '这个浏览器存不下草稿（无痕模式或者空间满了）。现在关掉页面，写的东西会丢。',
        name: '名字',
        nameLabel: '这条叫什么',
        nameHint: '投稿人写的那个只是起点，随你改。他原来写的会原样留在记录里，不会被盖掉。',
        captions: '三种语言的文案',
        captionsHint: '三个都手写。旁边是这个分类里已有的写法 —— 照着那个调子来，前缀别漏。',
        source: '来源信息',
        sourceHint: '可选。投稿人填的会预填在这里；不填也不影响通过或发布。',
        sourceKind: '来源类型',
        sourceTitle: '来源标题',
        sourceDate: '来源日期',
        sourceTime: '来源时间',
        sourceUrl: '来源链接',
        registerGroup: '分类自己的文案',
        registerSiblings: '同分类里已经上线的',
        registerNone: '这个分类还没有上线的条目',
        registerUnavailable: '读不到已上线的目录，这里给不了参照',
        charCount: '{n}/{max}',
        missingLocales: '还差这些语言：{locales}。现在也能通过，但发布会一直被拒，直到补齐。',
        group: '分类',
        groupAccept: '就按他说的放：{name}',
        groupReassign: '换一个已有的',
        groupPick: '挑一个…',
        groupRetired: '已停用',
        groupProposedUnusable: '他填的分类 {id} 用不了（不存在，或者已经停用），自己挑一个吧。',
        groupNew: '新建一个分类',
        groupNewName: '新分类叫什么',
        groupNewId: '新分类的 id（可以留空）',
        groupNewIdHint: '留空就自动生成一串 uuid：不好读，但绝不会撞，而且写下就改不了。要自己起就只能用小写字母、数字、减号和下划线，最多 64 位。',
        groupNewIdBad: '这个 id 用不了：小写字母、数字、减号、下划线，最多 64 位。',
        groupNewCaptions: '新分类的三种语言文案',
        groupNewMissing: '新分类还差 {locales}。一个分类第一次出现在页面上时缺文案，发布会被拒。',
        groupNameClash: '已经有个在用的分类叫这个名字了（{id}）。两个同名分类，是把音频归错地方最省事的办法。',
        decision: '定夺',
        reasonLabel: '给投稿人的话',
        reasonHint: '退回必须写；通过可以不写。这段话投稿人会原样看到，所以写成人话。',
        approve: '通过',
        reject: '退回',
        approveNeedsListen: '整条听完，通过按钮才会亮。这是故意的，不是卡住了。',
        approveBlocked: '还差这些：{reasons}',
        approveIsNotLive: '通过只是写进库里。站上要看得到，还得再点一次发布。',
        revise: '改判为通过',
        reviseLegend: '改判',
        reviseHint: '这条之前被退回了。下面填好名字、文案和分类，点「改判为通过」就会把它变成一条草稿音声（还要再发布才上线）。',
        approvedStaysDraft: '通过了，但它现在是 draft —— 站上还没有这个按钮。',
        blockName: '名字',
        blockNoCaption: '至少得写一种语言的文案',
        blockGroup: '还没定分类',
        blockGroupName: '新分类的名字',
        blockGroupId: '新分类的 id',
        blockReason: '给投稿人的话',
        resultApproved: '通过了。',
        resultRejected: '退回了。',
        resultSaved: '改好了。',
        batchResolved: '这一批全定完了。',
        reviewNext: '接着看下一条 →',
        saveChanges: '保存修改'
      },
      text: {
        empty: '这里不能空着',
        too_long: '超过 {max} 个字了',
        lone_surrogate: '有个落单的代理项字符（多半是粘贴带进来的）—— 存下去会整段变成乱码',
        control_character: '混进了控制字符',
        bidi_control: '混进了双向文字控制符：它能让显示出来的顺序跟存下去的不一样，你审的和别人看的会是两回事',
        invisible_character: '混进了看不见的零宽字符',
        linked_message: '不能有“＠”紧接着冒号这种写法。vue-i18n 会把它当成对别处文案的引用，然后把别人的内容拼进这句里。',
        escaped: '会按这样存：{stored}。vue-i18n 8 没有转义写法，大括号和竖线只能换成全角同形字 —— 不这么做，你写的字会被它悄悄吃掉。'
      },
      publish: {
        title: '发布',
        twoStage: '通过是写进库，发布才是上线。两件事，分开做。',
        recheck: '再查一次',
        waiting: '有 {count} 条已经通过、还没上线。',
        nothingWaiting: '没有等着上线的了。',
        refused: '现在发不了。先把下面这些处理掉：',
        existingHoles: '站上已经有的窟窿（不挡这次发布，但页面上就是那样显示的）：',
        asItStands: '站上现在是',
        catalogCounts: '{groups} 个分类 · {clips} 条音频',
        digest: '目录文件 sha256',
        writtenAt: '上次写于',
        verifySha: '逐个文件重算 sha256',
        verifyShaHint: '更实在，也更慢 —— 每个音频都要整个读一遍。平常对一下大小就够了。',
        go: '发布这 {count} 条',
        goHint: '先把 draft 全部转成已发布，再写 catalog.json。万一中间断了，库会走在文件前面 —— 再点一次就补上；反过来才是没法自己好的那种。',
        done: '发布好了：{count} 条上线，catalog.json 也重写了。',
        doneUnchanged: '发布完成：{count} 条。catalog.json 的内容没变，所以文件没有动。',
        blockRetired: '有音频要进一个已停用的分类：{id}',
        blockHole: '缺文案：{kind} {id} 的 {locale}',
        blockMedia: '音频文件对不上：{path}（{problem}）'
      },
      audit: {
        title: '操作记录',
        appendOnly: '只往后追加，最新的在最上面。写错了就补一条更正，原来那条永远留着。',
        filterVerb: '按动作筛',
        anyVerb: '全部',
        filterSubject: '按对象 id 筛',
        filterActor: '按谁筛（名字）',
        filterKind: '身份',
        anyKind: '全部',
        kind: { owner: '管理员', submitter: '投稿人', system: '系统' },
        filteringByActor: '只看这个人：{who}',
        clearActor: '取消',
        colAt: '时间',
        colActor: '谁',
        colVerb: '做了什么',
        colSubject: '对谁',
        colChange: '变化',
        refused: '被拒了',
        corrects: '这是对 {id} 的更正：',
        older: '再往前看',
        empty: '没有记录。'
      },
      clips: {
        title: '音声库',
        subtitle: '站点上所有的音声，可以下架 / 恢复。下架的会从站点消失，恢复即回。',
        counts: '共 {total} 条 · 在线 {published} · 已下架 {retired} · 草稿 {draft} · 零文案 {zeroCaptions}',
        filterState: '按状态筛',
        filterCaptions: '按文案完整度筛',
        filterSource: '按来源筛',
        filterSubmitter: '按投稿人筛',
        anyState: '全部',
        anyCaptionState: '全部',
        anySource: '全部',
        anySubmitter: '全部',
        complete: '文案齐全',
        incomplete: '文案不全',
        zeroCaptions: '没有文案',
        sourcePresent: '有来源',
        sourceMissing: '无来源',
        submitterPresent: '有投稿人',
        submitterMissing: '无投稿人',
        selected: '已选 {n} 条',
        movePick: '移动到分类…',
        move: '移动',
        clearSelection: '清除选择',
        selectAll: '全选当前筛选',
        state: { draft: '草稿', published: '在线', retired: '已下架' },
        colLabel: '名字',
        colGroup: '分类',
        colState: '状态',
        colCaptions: '文案',
        colSource: '来源',
        colSourceTime: '时间点',
        colSubmitter: '投稿人',
        colClip: '音频',
        colWhen: '时间',
        colAction: '操作',
        clipSize: '{seconds} 秒 · {kb} KB · {ext}',
        publishedAt: '上线于',
        retiredAt: '下架于',
        preview: '试听',
        playing: '播放中',
        retire: '下架',
        restore: '恢复',
        confirmRetire: '把「{label}」从站点下架？之后可以恢复。',
        empty: '这个筛选下没有音声。',
        saved: '已保存，站点目录已更新。',
        moved: '已把 {n} 条移到「{group}」。',
        edit: '编辑', sourceKind: '来源类型', sourceTitle: '来源标题', sourceDate: '来源日期', sourceTime: '来源时间', sourceUrl: '来源链接', blankDeletes: '留空删除这门语言', noSource: '无来源', noSubmitter: '无投稿人', sourceTimeBad: '时间请输入秒数、mm:ss 或 h:mm:ss。'
      },
      recycle: {
        title: '回收站',
        subtitle: '被退回的投稿。它们的音频还在，可以在这里改判为通过，或让它到期后被回收。',
        counts: '共 {count} 条 · 音频在退回 {days} 天后可被回收',
        empty: '没有被退回的投稿。',
        pick: '从左边挑一条，重新决定它。',
        colName: '名字',
        colClip: '音频',
        colRejectedAt: '退回于',
        colReason: '退回理由',
        colRetention: '剩余保留',
        colAction: '操作',
        noReason: '没写理由',
        retentionLeft: '还有 {days} 天',
        retentionDue: '待回收',
        retentionUnknown: '未知',
        retentionReclaimed: '已回收',
        retentionRetained: '保留中',
        revise: '改判',
        open: '正在看'
      },
      reclaim: {
        title: '存储回收',
        subtitle: '清理被驳回或废弃投稿的音频文件，释放磁盘空间。',
        empty: '暂无可回收的音频。',
        summary: '可回收 {count} 项，共 {size}。',
        retentionNote: '仅回收被驳回或废弃超过 {days} 天、且无任何引用的音频；已发布或已下架的音声永不在此列。',
        colSha: '文件（sha256）',
        colSize: '大小',
        colLastActivity: '最后活动',
        collect: '回收',
        confirm: '确认回收这 {count} 项音频（共 {size}）？将删除文件，不可撤销；被驳回的投稿从此无法改判。',
        resultDone: '已回收 {count} 项，释放 {size}。',
        resultFailures: '有 {n} 项文件未能删除（已标记，稍后重试即可）。'
      },
      storage: {
        title: '卷空间',
        unavailable: '暂时读不到卷空间状态。',
        available: '可用空间',
        used: '已用 / 总量',
        reserve: '保留底线',
        inflight: '进行中的预留',
        status: '投稿闸门',
        accepting: '正常受理',
        refusing: '空间不足，正在拒绝',
        lastRefusal: '上次拒绝',
        never: '没有记录',
        reclaimable: '可回收：{count} 项，共 {size}。'
      },
      branding: {
        title: '品牌',
        subtitle: '站点的名字、图标和频道链接。改完保存，下次打开页面就生效，不用重新构建。',
        navTitle: '导航栏标题',
        navTitleHint: '左上角显示的名字。留空则用站点自带的默认名。',
        docTitle: '网页标题',
        docTitleHint: '浏览器标签页显示的标题。留空则用默认。',
        channel: '频道链接文案',
        channelHint: '导航栏那个频道链接显示的文字。留空则用默认。',
        channelHref: '频道链接地址',
        favicon: '网站图标（favicon）',
        faviconHint: '同时用作浏览器标签图标和标题前的小图标。支持 PNG 或 ICO，最大 256 KB。',
        faviconNone: '还没有自定义图标，用的是自带的。',
        save: '保存',
        saved: '已保存，刷新页面即可看到。',
        faviconSaved: '图标已更新，刷新页面即可看到。（标题等未保存的改动仍保留在框里）'
      },
      theme: {
        title: '主题',
        delivery: '保存会重写卷上的 theme.css。站点下次加载页面时才生效 —— 你现在这个标签页不会自己变。',
        reload: '丢掉这里的改动，重新读一次',
        noRoster: '服务器没给出可改的颜色名册，这里就没有东西可填。',
        activeNone: '现在没有启用的主题。站上用的是打包进代码里的那套配色。',
        activeSince: '启用中：{name}（{at}）',
        nameLegend: '名字',
        nameLabel: '这套主题叫什么',
        nameHint: '给你自己看的，会跟着主题一起存下来。',
        colours: '颜色',
        coloursHint: '每一项都是站上一个真实的面。整套一起交，服务器会把十八对前景背景全算一遍 —— 有一对不过，整套就被拒，一个字节都不会写。',
        tokenPicker: '取色器：{label}',
        notColour: '这不像颜色。可以写 #rgb、#rrggbb 或者 #rrggbbaa。这只是这个页面自己的提醒，算不算数由服务器说。',
        wallpaper: '壁纸',
        wallpaperHint: '只铺在页面最底下。SVG 一律拒收；收下的图会在服务器上重新编码，传上去的字节不会原样发出去。背后有图时，正文那块底必须不透明。',
        wallpaperChoose: '选一张图',
        wallpaperUploading: '上传中…',
        wallpaperNone: '没有壁纸。',
        wallpaperCurrent: '当前壁纸',
        wallpaperShape: '尺寸',
        wallpaperMeta: '{width} × {height} · {kb} KB · {format}',
        wallpaperRemove: '不要壁纸',
        wallpaperPending: '壁纸这一项还没生效 —— 要按下面的保存。',
        preview: '预览',
        previewHint: '只是这几个面大概长什么样，不是判据。真正算数的，是保存时服务器算出来的那十八对数，还有各项下面的提示。',
        previewSubstituted: '这些项现在填的不是颜色，预览里临时用了内置值：{tokens}',
        previewHeader: '示例分类',
        previewClip: '示例音声',
        previewResting: '静止',
        previewHover: '悬停',
        previewBody: '正文坐在这块底上。',
        previewFooter: '页脚的字和链接',
        save: '保存并启用',
        saveHint: '整套一起提交。因为下面列出的那些问题被拒时，什么都不会动：库里不写行，theme.css 也不碰。',
        saved: '存好了，theme.css 已重写。站上刷新一次才看得到。',
        revert: '退回内置配色',
        revertHint: '把当前主题停用，theme.css 变回空的。那一行还留在库里，启用过的历史不会消失。',
        reverted: '已退回内置配色。上面填的还留着，想再来一次直接按保存。',
        refused: '这套主题被拒了。库里没写，theme.css 也没动：',
        problem: {
          unknown_token: '{token} 不是站上有的那几个颜色名，直接拒掉 —— 放行就等于让这个表单往样式表里写任意 CSS。',
          missing_token: '这一项没交上去。',
          not_a_colour: '不是颜色。可以写 #rgb、#rrggbb 或者 #rrggbbaa。',
          translucent: '半透明的颜色算不出对比度：它最后看着是什么色，取决于背后是什么。只有正文底那一项可以半透明，而且只在没壁纸的时候。',
          contrast: '对比度不够：',
          content_backing_translucent: '背后有壁纸时，正文那块底必须不透明 —— 不然正文读不读得清，取决于那张图这一块恰好是什么颜色。',
          name_empty: '给它起个名字。',
          name_too_long: '超过 {max} 个字了。',
          not_an_object: '服务器没读懂交上去的东西。',
          wallpaper_missing: '这张壁纸已经不在服务器上了。重新传一张，或者把壁纸清掉再保存。（从备份恢复之后最容易碰到：主题那条记录回来了，图没回来。）',
          wallpaper_malformed: '壁纸的文件名不对。壁纸只能通过上面的上传选，不能手填。',
          unknown: '被拒了：{code}'
        }
      }
    }
  },

  'en-US': {
    admin: {
      admins: {
        title: 'Admins',
        subtitle: 'Who can reach this desk. Seed admins come from the deployment config and cannot be removed; the rest were invited online.',
        refresh: 'Refresh',
        you: 'you',
        seed: 'seed',
        invited: 'invited',
        seedNote: 'from the deployment config, not removable here',
        remove: 'Remove',
        removing: 'Removing…',
        removeConfirm: 'Remove admin {id}? They lose the desk immediately.',
        inviteTitle: 'Invite a new admin',
        inviteLead: "Generate a phrase for them to post as an ordinary danmaku in this site's live room; after they post it, you confirm.",
        startInvite: 'Create an invite',
        opening: 'Connecting to the room…',
        postThis: 'Have them post this phrase in the live room:',
        openRoom: 'Open the live room',
        copied: 'copied',
        connecting: 'Connecting to the room, one moment…',
        expiresIn: '{s}s left',
        claimedBy: 'This phrase was claimed by:',
        confirmWarn: 'Before confirming, check this is the person you meant. Confirming makes them an admin at once.',
        confirm: 'Confirm — make admin',
        confirming: 'Confirming…',
        confirmedNow: '{id} is an admin now.',
        expired: 'This phrase expired. Create a new one.',
        done: 'Done',
        cancel: 'Cancel'
      },
      nav: {
        title: 'Review desk',
        twoStage: 'Approved is not live. Publishing is a separate step.',
        queue: 'Queue',
        publish: 'Publish',
        clips: 'Library',
        recycle: 'Recycle bin',
        audit: 'Log',
        storage: 'Storage',
        theme: 'Theme',
        branding: 'Branding',
        admins: 'Admins'
      },
      common: {
        refresh: 'Refresh',
        retry: 'Try again',
        loading: 'Loading…'
      },
      error: {
        unreachable: 'Cannot reach the backend. That is a network or a server problem, not a sign that you have been logged out.'
      },
      queue: {
        title: 'Waiting for review',
        order: 'Arrival order, and nothing else. First in, first seen.',
        counts: '{batches} batches · {items} clips',
        truncated: 'Showing the oldest {limit}. There are more behind them.',
        empty: 'Nothing waiting.',
        submittedAt: 'Sent',
        history: 'History: {approved} approved · {rejected} rejected · {submitted} submissions',
        drift: 'This submitter’s stored counters disagree with the database. The real figures are {approved} approved · {rejected} rejected · {submitted} submissions. Nothing repairs this automatically, because a silent repair would also hide whatever caused it.',
        blocked: 'This submitter is flagged as blocked. That limits submitting, not what you decide here.',
        sourceWeb: 'Source: web submission',
        sourceApi: 'Source: API · self-claimed “{label}”',
        sourceUnnamed: 'unnamed client',
        colPosition: '#',
        colName: 'Proposed name',
        colGroup: 'Proposed group',
        colClip: 'Audio',
        colNote: 'Note',
        colAction: 'Action',
        clipSize: '{seconds}s · {kb} KB · {ext}',
        noNote: 'no note',
        noGroup: 'none',
        proposedGroup: 'proposes a new group: {name}',
        review: 'Review',
        open: 'Open',
        heard: 'heard'
      },
      audition: {
        title: 'Listen first',
        length: '{seconds}s',
        play: 'Play',
        restart: 'Play from the start',
        pause: 'Pause',
        resume: 'Resume',
        volume: 'Volume',
        statusIdle: 'Not played yet',
        statusPlaying: 'Playing · {seconds}s',
        statusPaused: 'Paused at {seconds}s',
        statusHeard: 'Heard all the way through',
        statusStalled: 'The player has not reported the end',
        stalledHint: 'It may just be buffering, or the file may be unreachable or unplayable in this browser. Try again; if it stays like this, do not approve it.',
        statusTruncated: 'It stopped before the end',
        truncatedHint: 'Only {played}s played, against the {expected}s recorded when this was accepted. The file on the volume is not the file that was taken in — do not approve it; go and look at this clip on disk.',
        gate: 'Approve stays disabled until the clip finishes. The bar above is estimated from elapsed time; what actually counts is the player reporting that it ended. Pausing is fine — there is no scrubber here, so the only way to the end is through every second of it.'
      },
      desk: {
        title: 'This clip',
        pick: 'Pick something from the queue.',
        backToQueue: '← Back to the queue',
        backToRecycle: '← Back to the recycle bin',
        position: 'Item {position} of {count} in this batch',
        batchProgress: 'This batch: {pending} pending · {approved} approved · {rejected} rejected',
        proposedName: 'Name the submitter wrote',
        proposedGroup: 'Group the submitter wanted',
        submitterCaption: 'Caption the submitter wrote',
        submitterNote: 'Submitter’s note',
        submitterSource: 'Source the submitter provided',
        alreadyDecided: 'This item is already {state} ({at}).',
        notLiveYet: 'Approved, but not live — it is not on the site yet.',
        live: 'Live since {at}.',
        publishBlocked: 'Publishing will be refused until these are written: {locales}',
        goPublish: 'Go to Publish →',
        draftRestored: 'Restored the draft you left unfinished ({at}).',
        draftDiscard: 'Discard it and start from the submission',
        draftStorageFailed: 'This browser will not store drafts (private mode, or the quota is full). Close the page now and what you have typed is gone.',
        name: 'Name',
        nameLabel: 'What this clip is called',
        nameHint: 'The submitter’s wording is only a starting point. What they wrote is kept as-is in the record and is never overwritten.',
        captions: 'Captions, three languages',
        captionsHint: 'All three are typed by hand. Beside each field is what this group already says — match that register, and keep the prefix.',
        source: 'Source information',
        sourceHint: 'Optional. The submitter’s fields are prefilled here; leaving them blank does not block approval or publishing.',
        sourceKind: 'Source type',
        sourceTitle: 'Source title',
        sourceDate: 'Source date',
        sourceTime: 'Source time',
        sourceUrl: 'Source URL',
        registerGroup: 'The group’s own caption',
        registerSiblings: 'Already published in this group',
        registerNone: 'Nothing published in this group yet',
        registerUnavailable: 'The published catalogue could not be read, so there is nothing to match against here',
        charCount: '{n}/{max}',
        missingLocales: 'Still missing: {locales}. You can approve now, but publishing will be refused until they are filled.',
        group: 'Group',
        groupAccept: 'Accept what they proposed: {name}',
        groupReassign: 'Move it to an existing group',
        groupPick: 'Pick one…',
        groupRetired: 'retired',
        groupProposedUnusable: 'The group they named ({id}) cannot be used — it does not exist, or it is retired. Pick one yourself.',
        groupNew: 'Approve a new group',
        groupNewName: 'What the new group is called',
        groupNewId: 'Id for the new group (optional)',
        groupNewIdHint: 'Leave it empty and a uuid is minted: bad to read, impossible to collide, and fixed forever once written. If you type one it may hold lowercase letters, digits, hyphen and underscore only, up to 64 characters.',
        groupNewIdBad: 'Not usable as an id: lowercase letters, digits, hyphen and underscore only, up to 64 characters.',
        groupNewCaptions: 'Captions for the new group',
        groupNewMissing: 'The new group is still missing {locales}. A group arriving on the page with a caption missing is refused at publish.',
        groupNameClash: 'A live group is already called this ({id}). Two groups with one visible name is how a clip ends up filed under the wrong one.',
        decision: 'Decision',
        reasonLabel: 'Message to the submitter',
        reasonHint: 'Required to reject, optional to approve. The submitter reads this exactly as you write it.',
        approve: 'Approve',
        reject: 'Reject',
        approveNeedsListen: 'Approve unlocks once you have heard the whole clip. That is deliberate, not a stuck button.',
        approveBlocked: 'Still missing: {reasons}',
        approveIsNotLive: 'Approving writes to the database. Getting it onto the site takes a separate publish.',
        revise: 'Approve after all',
        reviseLegend: 'Revise',
        reviseHint: 'This was rejected. Fill in the name, captions and group below and click "Approve after all" to turn it into a draft clip (still needs a publish to go live).',
        approvedStaysDraft: 'Approved — but it is a draft. There is no button for it on the site yet.',
        blockName: 'the name',
        blockNoCaption: 'at least one caption',
        blockGroup: 'no group chosen',
        blockGroupName: 'the new group’s name',
        blockGroupId: 'the new group’s id',
        blockReason: 'the message to the submitter',
        resultApproved: 'Approved.',
        resultRejected: 'Rejected.',
        resultSaved: 'Saved.',
        batchResolved: 'Every item in this batch is now decided.',
        reviewNext: 'Review the next one →',
        saveChanges: 'Save changes'
      },
      text: {
        empty: 'This cannot be empty',
        too_long: 'Longer than {max} characters',
        lone_surrogate: 'There is an unpaired surrogate in here, usually from a paste. Stored, it corrupts the whole line rather than shortening it.',
        control_character: 'There is a control character in here',
        bidi_control: 'There is a bidirectional control character in here. Its whole purpose is to make the rendered order differ from the stored order, so you would be approving one string while visitors are shown another.',
        invisible_character: 'There is a zero-width character in here',
        linked_message: 'An at-sign immediately followed by a colon (＠：) is not allowed. vue-i18n reads it as a reference to another message and splices someone else’s text into this one.',
        escaped: 'Stored as: {stored}. vue-i18n 8 has no escape syntax, so curly braces and the pipe are substituted with fullwidth look-alikes — without that, your own words are the ones that disappear.'
      },
      publish: {
        title: 'Publish',
        twoStage: 'Approving writes to the database. Publishing is what puts it on the site.',
        recheck: 'Check again',
        waiting: '{count} approved clips are not on the site yet.',
        nothingWaiting: 'Nothing is waiting to go live.',
        refused: 'Cannot publish yet. These have to be dealt with first:',
        existingHoles: 'Holes already on the site. They do not block this publish, but that is how the page renders them:',
        asItStands: 'On the site right now',
        catalogCounts: '{groups} groups · {clips} clips',
        digest: 'Catalogue sha256',
        writtenAt: 'Last written',
        verifySha: 'Re-hash every audio file',
        verifyShaHint: 'Honest, and slow — it reads every blob end to end. Comparing sizes is enough most days.',
        go: 'Publish these {count}',
        goHint: 'Promotes every draft, then writes catalog.json. If it breaks in between, the database ends up ahead of the file and the next publish fixes it. The other order is the one nothing corrects.',
        done: 'Published: {count} clips are live and catalog.json was rewritten.',
        doneUnchanged: 'Published: {count} clips. catalog.json already held those exact bytes, so the file was left alone.',
        blockRetired: 'A clip is waiting in a retired group: {id}',
        blockHole: 'Missing caption: {kind} {id}, {locale}',
        blockMedia: 'Audio does not match the database: {path} ({problem})'
      },
      audit: {
        title: 'Log',
        appendOnly: 'Append-only, newest first. A correction is a new entry; the original always stays.',
        filterVerb: 'Filter by action',
        anyVerb: 'Any',
        filterSubject: 'Filter by subject id',
        filterActor: 'Filter by name',
        filterKind: 'Role',
        anyKind: 'Any',
        kind: { owner: 'Admin', submitter: 'Submitter', system: 'System' },
        filteringByActor: 'Only this person: {who}',
        clearActor: 'clear',
        colAt: 'When',
        colActor: 'Who',
        colVerb: 'Action',
        colSubject: 'Subject',
        colChange: 'Change',
        refused: 'refused',
        corrects: 'Corrects {id}:',
        older: 'Load older',
        empty: 'Nothing recorded.'
      },
      clips: {
        title: 'Library',
        subtitle: 'Every clip the site has. Take one down (retire) or put it back (restore); a retired clip leaves the site and returns on restore.',
        counts: '{total} total · {published} live · {retired} retired · {draft} draft · {zeroCaptions} with no captions',
        filterState: 'Filter by state',
        filterCaptions: 'Filter by caption completeness',
        filterSource: 'Filter by source',
        filterSubmitter: 'Filter by submitter',
        anyState: 'Any',
        anyCaptionState: 'Any',
        anySource: 'Any',
        anySubmitter: 'Any',
        complete: 'complete',
        incomplete: 'incomplete',
        zeroCaptions: 'no captions',
        sourcePresent: 'with source',
        sourceMissing: 'without source',
        submitterPresent: 'with submitter',
        submitterMissing: 'without submitter',
        selected: '{n} selected',
        movePick: 'Move to group…',
        move: 'Move',
        clearSelection: 'Clear selection',
        selectAll: 'Select all filtered clips',
        state: { draft: 'draft', published: 'live', retired: 'retired' },
        colLabel: 'Name',
        colGroup: 'Group',
        colState: 'State',
        colCaptions: 'Captions',
        colSource: 'Source',
        colSourceTime: 'Time point',
        colSubmitter: 'Submitter',
        colClip: 'Clip',
        colWhen: 'When',
        colAction: 'Action',
        clipSize: '{seconds}s · {kb} KB · {ext}',
        publishedAt: 'live since',
        retiredAt: 'retired',
        preview: 'Play',
        playing: 'Playing',
        retire: 'Retire',
        restore: 'Restore',
        confirmRetire: 'Take "{label}" off the site? You can restore it later.',
        empty: 'No clips under this filter.',
        saved: 'Saved; the site catalogue is updated.',
        moved: 'Moved {n} clips to “{group}”.',
        edit: 'Edit', sourceKind: 'Source type', sourceTitle: 'Source title', sourceDate: 'Source date', sourceTime: 'Source time', sourceUrl: 'Source URL', blankDeletes: 'Blank removes this locale', noSource: 'no source', noSubmitter: 'no submitter', sourceTimeBad: 'Use seconds, mm:ss or h:mm:ss for the time.'
      },
      recycle: {
        title: 'Recycle bin',
        subtitle: 'Rejected submissions. Their audio is still here; overturn a rejection into an approval, or let it age out and be reclaimed.',
        counts: '{count} total · audio is reclaimable {days} days after a rejection',
        empty: 'Nothing has been rejected.',
        pick: 'Pick one on the left to decide it again.',
        colName: 'Name',
        colClip: 'Clip',
        colRejectedAt: 'Rejected',
        colReason: 'Reason',
        colRetention: 'Retention',
        colAction: 'Action',
        noReason: 'no reason given',
        retentionLeft: '{days} days left',
        retentionDue: 'due for cleanup',
        retentionUnknown: 'unknown',
        retentionReclaimed: 'reclaimed',
        retentionRetained: 'retained',
        revise: 'Revise',
        open: 'Open'
      },
      reclaim: {
        title: 'Storage reclaim',
        subtitle: 'Remove the audio of rejected and abandoned submissions to free disk space.',
        empty: 'Nothing to reclaim right now.',
        summary: '{count} reclaimable, {size} in all.',
        retentionNote: 'Only audio rejected or abandoned more than {days} days ago and referenced by nothing is listed; a published or retired clip’s audio is never here.',
        colSha: 'File (sha256)',
        colSize: 'Size',
        colLastActivity: 'Last activity',
        collect: 'Reclaim',
        confirm: 'Reclaim these {count} audio files ({size})? This deletes the files and cannot be undone; rejected submissions can no longer be revised.',
        resultDone: 'Reclaimed {count}, freed {size}.',
        resultFailures: '{n} file(s) could not be deleted (marked; a re-run will retry).'
      },
      storage: {
        title: 'Volume storage',
        unavailable: 'The volume status is temporarily unavailable.',
        available: 'Available',
        used: 'Used / total',
        reserve: 'Hard reserve',
        inflight: 'In-flight reservations',
        status: 'Submission gate',
        accepting: 'Accepting submissions',
        refusing: 'Low space; refusing submissions',
        lastRefusal: 'Last refusal',
        never: 'None recorded',
        reclaimable: 'Reclaimable: {count} item(s), {size} total.'
      },
      branding: {
        title: 'Branding',
        subtitle: "The site's name, icon and channel link. Saved changes are live on the next page load — no rebuild.",
        navTitle: 'Navbar title',
        navTitleHint: 'The name shown top-left. Leave blank to use the built-in default.',
        docTitle: 'Page title',
        docTitleHint: "The browser tab's title. Leave blank to use the default.",
        channel: 'Channel link text',
        channelHint: 'The wording of the channel link in the navbar. Leave blank to use the default.',
        channelHref: 'Channel link URL',
        favicon: 'Favicon',
        faviconHint: 'Used both as the browser-tab icon and the little mark before the title. PNG or ICO, up to 256 KB.',
        faviconNone: 'No custom icon yet; the built-in one is in use.',
        save: 'Save',
        saved: 'Saved. Reload a page to see it.',
        faviconSaved: 'Icon updated. Reload a page to see it. (Any unsaved text edits are still in the boxes.)'
      },
      theme: {
        title: 'Theme',
        delivery: 'Saving rewrites theme.css on the volume. The site picks it up on its next page load, so this tab will not change by itself.',
        reload: 'Discard these edits and re-read',
        noRoster: 'The server did not send the roster of colours, so there is nothing here to fill in.',
        activeNone: 'No theme is active. The site is showing the palette compiled into its bundle.',
        activeSince: 'Active: {name} ({at})',
        nameLegend: 'Name',
        nameLabel: 'What this theme is called',
        nameHint: 'For you. It is kept with the stored theme.',
        colours: 'Colours',
        coloursHint: 'Every one of these is a real surface on the site. The palette goes as one piece and the server measures all eighteen foreground and background pairs — one failing pair refuses the whole thing, and nothing is written.',
        tokenPicker: 'Colour picker for {label}',
        notColour: 'That does not look like a colour. Write #rgb, #rrggbb or #rrggbbaa. This is only this page talking; the server has the last word.',
        wallpaper: 'Wallpaper',
        wallpaperHint: 'It only ever sits behind the page. SVG is refused, and what you upload is re-encoded on the server, so the bytes you send are not the bytes that get served. With a picture back there, the backing under the content has to be opaque.',
        wallpaperChoose: 'Pick an image',
        wallpaperUploading: 'Uploading…',
        wallpaperNone: 'No wallpaper.',
        wallpaperCurrent: 'Current wallpaper',
        wallpaperShape: 'Size',
        wallpaperMeta: '{width} × {height} · {kb} KB · {format}',
        wallpaperRemove: 'Remove the wallpaper',
        wallpaperPending: 'The wallpaper is not applied yet — save below.',
        preview: 'Preview',
        previewHint: 'An impression of a few surfaces, not the verdict. What counts is the eighteen ratios the server computes when you save, and the messages under the fields.',
        previewSubstituted: 'These are not colours yet, so the preview is standing in the built-in value for them: {tokens}',
        previewHeader: 'Example category',
        previewClip: 'Example clip',
        previewResting: 'resting',
        previewHover: 'hover',
        previewBody: 'Body text sits on this backing.',
        previewFooter: 'Footer text and links',
        save: 'Save and activate',
        saveHint: 'The whole palette goes at once. Refused for one of the problems listed below, it changes nothing: no row is stored and theme.css is not touched.',
        saved: 'Saved, and theme.css was rewritten. Reload the site to see it.',
        revert: 'Back to the built-in palette',
        revertHint: 'Deactivates the current theme and empties theme.css. The row stays, so the record of when it was active does not disappear.',
        reverted: 'Back on the built-in palette. What is typed above is still here — press save to put it back.',
        refused: 'The theme was refused. Nothing was stored and theme.css was not touched:',
        problem: {
          unknown_token: '{token} is not one of the site’s colours, and it is refused rather than passed through: allowing it would let this form write arbitrary CSS into the stylesheet.',
          missing_token: 'This one was not submitted.',
          not_a_colour: 'Not a colour. Write #rgb, #rrggbb or #rrggbbaa.',
          translucent: 'A see-through colour has no single contrast ratio: what it ends up looking like depends on whatever is behind it. Only the backing under the content may be see-through, and only while there is no wallpaper.',
          contrast: 'Contrast is too low:',
          content_backing_translucent: 'With a wallpaper behind the page, the backing under the content has to be opaque — otherwise whether the text can be read depends on what that part of the picture happens to be.',
          name_empty: 'Give it a name.',
          name_too_long: 'Longer than {max} characters.',
          not_an_object: 'The server could not read the submitted theme at all.',
          wallpaper_missing: 'That wallpaper is no longer on the server. Upload it again, or clear the wallpaper and save without one. (Most likely after restoring a backup: the theme’s row comes back, the picture does not.)',
          wallpaper_malformed: 'That is not a wallpaper name this server will take. Pick a wallpaper with the upload above rather than typing one.',
          unknown: 'Refused: {code}'
        }
      }
    }
  },

  'ja-JP': {
    admin: {
      admins: {
        title: '管理者',
        subtitle: 'この管理画面に入れる人。シード管理者はデプロイ設定由来で削除できません。他はオンラインで招待された人です。',
        refresh: '更新',
        you: 'あなた',
        seed: 'シード',
        invited: '招待',
        seedNote: 'デプロイ設定由来、ここでは削除不可',
        remove: '削除',
        removing: '削除中…',
        removeConfirm: '管理者 {id} を削除しますか？すぐに管理画面へ入れなくなります。',
        inviteTitle: '新しい管理者を招待',
        inviteLead: 'フレーズを生成し、当サイトの配信部屋にふつうの弾幕として流してもらいます。流したら、あなたが確認します。',
        startInvite: '招待を作成',
        opening: '配信部屋に接続中…',
        postThis: 'このフレーズを配信部屋に流してもらってください：',
        openRoom: '配信部屋を開く',
        copied: 'コピーしました',
        connecting: '配信部屋に接続中です。少々お待ちを…',
        expiresIn: '残り {s} 秒',
        claimedBy: 'このフレーズは次の身分に取得されました：',
        confirmWarn: '確認の前に、招きたい相手かどうか確かめてください。確認すると即座に管理者になります。',
        confirm: '確認して管理者にする',
        confirming: '確認中…',
        confirmedNow: '{id} は管理者になりました。',
        expired: 'このフレーズは期限切れです。新しく作成してください。',
        done: '完了',
        cancel: 'キャンセル'
      },
      nav: {
        title: 'レビュー台',
        twoStage: '承認しただけでは公開されません',
        queue: '一覧',
        publish: '公開',
        clips: '音声庫',
        recycle: 'ごみ箱',
        audit: '記録',
        storage: 'ストレージ',
        theme: 'テーマ',
        branding: 'ブランド',
        admins: '管理者'
      },
      common: {
        refresh: '読み直す',
        retry: 'もう一度',
        loading: '読み込み中…'
      },
      error: {
        unreachable: 'バックエンドにつながりません。通信かサーバー側の問題で、ログアウトされたわけではありません。'
      },
      queue: {
        title: '未処理の投稿',
        order: '届いた順です。ほかの並べ替えはありません。',
        counts: '{batches} 件 · 音声 {items} 本',
        truncated: '古いほうから {limit} 本だけ表示しています。まだ続きがあります。',
        empty: 'いまは何もありません。',
        submittedAt: '送信',
        history: 'これまで：承認 {approved} · 却下 {rejected} · 投稿 {submitted} 回',
        drift: 'この人の集計値が実際とずれています。実際は承認 {approved} · 却下 {rejected} · 投稿 {submitted} 回です。自動では直しません。黙って直すと、ずれた原因まで一緒に隠れてしまうからです。',
        blocked: 'この投稿者には blocked が付いています。投稿側の制限で、ここでの判断には関係ありません。',
        sourceWeb: 'ソース：ウェブ投稿',
        sourceApi: 'ソース：API · 自称「{label}」',
        sourceUnnamed: '名前なしクライアント',
        colPosition: '番',
        colName: '投稿者がつけた名前',
        colGroup: '希望のグループ',
        colClip: '音声',
        colNote: 'ひとこと',
        colAction: '操作',
        clipSize: '{seconds} 秒 · {kb} KB · {ext}',
        noNote: 'なし',
        noGroup: '指定なし',
        proposedGroup: '新しいグループの提案：{name}',
        review: '見る',
        open: '表示中',
        heard: '聴了'
      },
      audition: {
        title: 'まず聴く',
        length: '{seconds} 秒',
        play: '再生',
        restart: '最初から',
        pause: '一時停止',
        resume: '続きから',
        volume: '音量',
        statusIdle: 'まだ聴いていません',
        statusPlaying: '再生中 · {seconds} 秒',
        statusPaused: '{seconds} 秒で停止中',
        statusHeard: '最後まで聴きました',
        statusStalled: 'プレイヤーから終了の合図が来ていません',
        stalledHint: '読み込み待ちかもしれませんし、ファイルが取れない、このブラウザでは再生できない、ということもあります。もう一度試して、変わらないようなら承認しないでください。',
        statusTruncated: '最後まで行かずに止まりました',
        truncatedHint: '再生できたのは {played} 秒で、受け取ったときの記録は {expected} 秒です。ボリューム上のファイルが受け取ったものと違います。承認せず、この音声のファイルを確認してください。',
        gate: '最後まで再生し終えるまで、承認ボタンは押せません。上のバーは経過時間からの目安で、実際の判定に使うのはプレイヤーが返す「終わった」の合図だけです。途中で止めても構いません。シークバーがないので、最後にたどり着く道は一本しかありません。'
      },
      desk: {
        title: 'この音声',
        pick: '一覧から一つ選んでください。',
        backToQueue: '← 一覧に戻る',
        backToRecycle: '← ごみ箱に戻る',
        position: 'この束の {count} 本中 {position} 本目',
        batchProgress: 'この束：未処理 {pending} · 承認 {approved} · 却下 {rejected}',
        proposedName: '投稿者がつけた名前',
        proposedGroup: '投稿者が希望したグループ',
        submitterCaption: '投稿者が書いた字幕',
        submitterNote: '投稿者のひとこと',
        submitterSource: '投稿者が提供した出典',
        alreadyDecided: 'これはすでに {state} です（{at}）。',
        notLiveYet: '承認済みですが、まだ公開されていません。サイトには出ていません。',
        live: '{at} から公開中です。',
        publishBlocked: 'この言語の文が足りないので、公開は拒否されます：{locales}',
        goPublish: '公開へ →',
        draftRestored: '書きかけの下書きを復元しました（{at}）。',
        draftDiscard: '下書きを捨てて投稿内容に戻す',
        draftStorageFailed: 'このブラウザでは下書きを保存できません（プライベートモードか、容量がいっぱいです）。いま閉じると書いた内容は消えます。',
        name: '名前',
        nameLabel: 'この音声の名前',
        nameHint: '投稿者の書いた名前はあくまで出発点です。書かれた文面はそのまま記録に残り、上書きされることはありません。',
        captions: '三か国語の文',
        captionsHint: '三つとも手で書きます。横にあるのはこのグループの既存の書き方です。同じ調子に揃えて、接頭辞を落とさないように。',
        source: '出典情報',
        sourceHint: '任意です。投稿者の入力はここに仮入力されます。空欄でも承認・公開は止まりません。',
        sourceKind: '出典の種類',
        sourceTitle: '出典のタイトル',
        sourceDate: '出典の日付',
        sourceTime: '出典の時間',
        sourceUrl: '出典リンク',
        registerGroup: 'グループ自体の文',
        registerSiblings: '同じグループで公開済みのもの',
        registerNone: 'このグループにはまだ公開済みのものがありません',
        registerUnavailable: '公開済みの目録が読めないので、ここに並べる見本がありません',
        charCount: '{n}/{max}',
        missingLocales: '{locales} がまだです。いま承認しても構いませんが、埋まるまで公開は拒否され続けます。',
        group: 'グループ',
        groupAccept: '希望どおりに入れる：{name}',
        groupReassign: '既存の別のグループに移す',
        groupPick: '選んでください…',
        groupRetired: '停止中',
        groupProposedUnusable: '希望されたグループ（{id}）は使えません。存在しないか、停止済みです。こちらで選んでください。',
        groupNew: '新しいグループを作る',
        groupNewName: '新しいグループの名前',
        groupNewId: '新しいグループの id（空でも可）',
        groupNewIdHint: '空にすると uuid が振られます。読みにくい代わりに衝突せず、一度決めたら変えられません。自分で付ける場合は英小文字・数字・ハイフン・アンダースコアのみ、64 文字までです。',
        groupNewIdBad: 'この id は使えません。英小文字・数字・ハイフン・アンダースコアのみ、64 文字までです。',
        groupNewCaptions: '新しいグループの三か国語の文',
        groupNewMissing: '新しいグループに {locales} が足りません。グループが初めてページに出るときに文が欠けていると、公開は拒否されます。',
        groupNameClash: '同じ名前の稼働中グループがすでにあります（{id}）。見た目が同じグループが二つあると、音声はまず間違ったほうに入ります。',
        decision: '判断',
        reasonLabel: '投稿者へのことば',
        reasonHint: '却下には必須、承認では任意です。書いたとおりに投稿者が読みます。',
        approve: '承認',
        reject: '却下',
        approveNeedsListen: '最後まで聴くと承認ボタンが押せるようになります。故障ではなく、そういう作りです。',
        approveBlocked: 'まだ足りません：{reasons}',
        approveIsNotLive: '承認はデータベースに書くところまでです。サイトに出すには、別に公開の操作が要ります。',
        revise: '承認に変更',
        reviseLegend: '再判定',
        reviseHint: 'これは却下されていました。下で名前・字幕・分類を入力し「承認に変更」を押すと、下書きの音声になります（公開するとサイトに出ます）。',
        approvedStaysDraft: '承認しました。ただしまだ下書きの状態で、サイトにボタンは出ていません。',
        blockName: '名前',
        blockNoCaption: '文が一つも入っていません',
        blockGroup: 'グループが未選択',
        blockGroupName: '新しいグループの名前',
        blockGroupId: '新しいグループの id',
        blockReason: '投稿者へのことば',
        resultApproved: '承認しました。',
        resultRejected: '却下しました。',
        resultSaved: '保存しました。',
        batchResolved: 'この束はすべて処理済みになりました。',
        reviewNext: '次の一本へ →',
        saveChanges: '変更を保存'
      },
      text: {
        empty: '空のままにはできません',
        too_long: '{max} 文字を超えています',
        lone_surrogate: '対になっていないサロゲート文字が入っています。貼り付けで紛れ込むことが多く、そのまま保存すると行全体が壊れます。',
        control_character: '制御文字が入っています',
        bidi_control: '双方向制御文字が入っています。表示順と保存内容をわざと食い違わせるための文字なので、あなたが承認した文と読み手が見る文が別物になります。',
        invisible_character: '幅ゼロの見えない文字が入っています',
        linked_message: 'アットマークのすぐ後ろにコロンを置く書き方（＠：）は使えません。vue-i18n が別の文への参照とみなして、他人の文章をここに差し込んでしまいます。',
        escaped: 'この形で保存されます：{stored}。vue-i18n 8 にはエスケープ記法がないため、波かっこと縦棒は全角の同形文字に置き換えます。そうしないと、消えるのはあなたが書いた言葉のほうです。'
      },
      publish: {
        title: '公開',
        twoStage: '承認はデータベースへの書き込み、公開がサイトに出す操作です。',
        recheck: 'もう一度確認',
        waiting: '承認済みでまだサイトに出ていない音声が {count} 本あります。',
        nothingWaiting: '公開待ちはありません。',
        refused: 'まだ公開できません。先に次を片付けてください：',
        existingHoles: 'すでにサイトにある欠けです。今回の公開は止めませんが、ページではこのまま表示されます：',
        asItStands: 'いまサイトにあるもの',
        catalogCounts: 'グループ {groups} · 音声 {clips}',
        digest: '目録の sha256',
        writtenAt: '最後に書いた時刻',
        verifySha: '音声ファイルを一つずつ再ハッシュする',
        verifyShaHint: '確実ですが遅くなります。全ファイルを最後まで読むためです。普段はサイズの照合で足ります。',
        go: 'この {count} 本を公開',
        goHint: '下書きをすべて公開済みにしてから catalog.json を書きます。途中で失敗するとデータベースがファイルより先に進みますが、次の公開で追いつきます。逆向きだけは誰も直せません。',
        done: '公開しました。{count} 本が反映され、catalog.json も書き直しました。',
        doneUnchanged: '公開しました（{count} 本）。catalog.json の中身は同じだったので、ファイルはそのままです。',
        blockRetired: '停止中のグループに入ろうとしている音声があります：{id}',
        blockHole: '文が足りません：{kind} {id} の {locale}',
        blockMedia: '音声ファイルがデータベースと一致しません：{path}（{problem}）'
      },
      audit: {
        title: '記録',
        appendOnly: '追記のみ、新しい順です。訂正は新しい一行として足すもので、元の行はずっと残ります。',
        filterVerb: '操作で絞る',
        anyVerb: 'すべて',
        filterSubject: '対象 id で絞る',
        filterActor: '名前で絞る',
        filterKind: '種別',
        anyKind: 'すべて',
        kind: { owner: '管理人', submitter: '投稿者', system: 'システム' },
        filteringByActor: 'この人だけ：{who}',
        clearActor: '解除',
        colAt: '時刻',
        colActor: '誰が',
        colVerb: '操作',
        colSubject: '対象',
        colChange: '変化',
        refused: '拒否',
        corrects: '{id} の訂正です：',
        older: 'さらに古いものを読む',
        empty: '記録はありません。'
      },
      clips: {
        title: '音声庫',
        subtitle: 'サイトにあるすべての音声です。取り下げ／戻すができます。取り下げるとサイトから消え、戻すと再び現れます。',
        counts: '全 {total} 件 · 公開 {published} · 取り下げ {retired} · 下書き {draft} · 字幕なし {zeroCaptions}',
        filterState: '状態で絞る',
        filterCaptions: '字幕の充足度で絞る',
        filterSource: '出典で絞る',
        filterSubmitter: '投稿者で絞る',
        anyState: 'すべて',
        anyCaptionState: 'すべて',
        anySource: 'すべて',
        anySubmitter: 'すべて',
        complete: '字幕あり',
        incomplete: '字幕不足',
        zeroCaptions: '字幕なし',
        sourcePresent: '出典あり',
        sourceMissing: '出典なし',
        submitterPresent: '投稿者あり',
        submitterMissing: '投稿者なし',
        selected: '{n} 件選択中',
        movePick: '移動先の分類…',
        move: '移動',
        clearSelection: '選択を解除',
        selectAll: '表示中をすべて選択',
        state: { draft: '下書き', published: '公開', retired: '取り下げ' },
        colLabel: '名前',
        colGroup: '分類',
        colState: '状態',
        colCaptions: '字幕',
        colSource: '出典',
        colSourceTime: '時間点',
        colSubmitter: '投稿者',
        colClip: '音声',
        colWhen: '日時',
        colAction: '操作',
        clipSize: '{seconds} 秒 · {kb} KB · {ext}',
        publishedAt: '公開',
        retiredAt: '取り下げ',
        preview: '試聴',
        playing: '再生中',
        retire: '取り下げ',
        restore: '戻す',
        confirmRetire: '「{label}」をサイトから取り下げますか？あとで戻せます。',
        empty: 'この絞り込みでは音声がありません。',
        saved: '保存しました。サイトのカタログを更新しました。',
        moved: '{n} 件を「{group}」へ移動しました。',
        edit: '編集', sourceKind: '出典の種類', sourceTitle: '出典のタイトル', sourceDate: '出典の日付', sourceTime: '出典の時間', sourceUrl: '出典リンク', blankDeletes: '空欄にするとこの言語を削除', noSource: '出典なし', noSubmitter: '投稿者なし', sourceTimeBad: '秒数、mm:ss、または h:mm:ss で入力してください。'
      },
      recycle: {
        title: 'ごみ箱',
        subtitle: '却下された投稿です。音声はまだ残っており、ここで承認に変更するか、期限切れで回収されるのを待てます。',
        counts: '全 {count} 件 · 音声は却下から {days} 日後に回収対象',
        empty: '却下された投稿はありません。',
        pick: '左から一つ選んで判定し直します。',
        colName: '名前',
        colClip: '音声',
        colRejectedAt: '却下日時',
        colReason: '却下理由',
        colRetention: '残り保持',
        colAction: '操作',
        noReason: '理由なし',
        retentionLeft: '残り {days} 日',
        retentionDue: '回収待ち',
        retentionUnknown: '不明',
        retentionReclaimed: '回収済み',
        retentionRetained: '保持中',
        revise: '再判定',
        open: '表示中'
      },
      reclaim: {
        title: 'ストレージ回収',
        subtitle: '却下・放棄された投稿の音声ファイルを削除し、ディスク容量を空けます。',
        empty: '今のところ回収できるものはありません。',
        summary: '回収可能 {count} 件、合計 {size}。',
        retentionNote: '却下または放棄から {days} 日を過ぎ、どこからも参照されていない音声だけが対象です。公開中・取り下げ済みの音声は決して含まれません。',
        colSha: 'ファイル（sha256）',
        colSize: 'サイズ',
        colLastActivity: '最終アクティビティ',
        collect: '回収',
        confirm: 'この {count} 件（合計 {size}）を回収しますか？ファイルを削除し、元に戻せません。却下された投稿は以後、再判定できなくなります。',
        resultDone: '{count} 件を回収し、{size} を解放しました。',
        resultFailures: '{n} 件のファイルを削除できませんでした（記録済み、再実行で再試行します）。'
      },
      storage: {
        title: 'ボリューム容量',
        unavailable: 'ボリュームの状態を一時的に取得できません。',
        available: '空き容量',
        used: '使用済み / 合計',
        reserve: '固定の予約容量',
        inflight: '処理中の予約',
        status: '投稿ゲート',
        accepting: '投稿を受け付け中',
        refusing: '容量不足、投稿を拒否中',
        lastRefusal: '最後の拒否',
        never: '記録なし',
        reclaimable: '回収可能：{count} 件、合計 {size}。'
      },
      branding: {
        title: 'ブランド',
        subtitle: 'サイトの名前・アイコン・チャンネルリンク。保存すると次のページ表示から反映され、再ビルドは不要です。',
        navTitle: 'ナビ見出し',
        navTitleHint: '左上に出る名前です。空欄なら既定の名前を使います。',
        docTitle: 'ページタイトル',
        docTitleHint: 'ブラウザのタブに出るタイトルです。空欄なら既定を使います。',
        channel: 'チャンネルリンクの文言',
        channelHint: 'ナビのチャンネルリンクに出す文字です。空欄なら既定を使います。',
        channelHref: 'チャンネルリンクの URL',
        favicon: 'ファビコン',
        faviconHint: 'タブのアイコンと、見出し前の小さなマークの両方に使います。PNG または ICO、最大 256 KB。',
        faviconNone: 'カスタムアイコンはまだなく、既定のものを使用中です。',
        save: '保存',
        saved: '保存しました。ページを再読み込みすると反映されます。',
        faviconSaved: 'アイコンを更新しました。ページを再読み込みすると反映されます。（未保存のテキスト編集は入力欄に残っています）'
      },
      theme: {
        title: 'テーマ',
        delivery: '保存するとボリューム上の theme.css を書き直します。反映されるのはサイトの次のページ読み込みからで、いま開いているこのタブが勝手に変わることはありません。',
        reload: 'ここでの変更を捨てて読み直す',
        noRoster: '変更できる色の一覧がサーバーから届いていないので、ここには入力するものがありません。',
        activeNone: '有効なテーマはありません。サイトはビルドに埋め込まれた配色を使っています。',
        activeSince: '有効：{name}（{at}）',
        nameLegend: '名前',
        nameLabel: 'このテーマの名前',
        nameHint: '自分用の目印です。保存したテーマと一緒に残ります。',
        colours: '色',
        coloursHint: 'どれもサイト上の実際の面です。配色はまとめて送られ、サーバーが十八組の前景と背景をすべて測ります。一組でも足りなければ全体が拒否され、何も書き込まれません。',
        tokenPicker: 'カラーピッカー：{label}',
        notColour: '色に見えません。#rgb、#rrggbb、#rrggbbaa のいずれかで書いてください。これはこの画面が言っているだけで、最後に決めるのはサーバーです。',
        wallpaper: '壁紙',
        wallpaperHint: 'ページのいちばん後ろにだけ敷かれます。SVG は受け付けません。受け取った画像はサーバー側で再エンコードするので、送ったバイトがそのまま配信されるわけではありません。背景に画像があるときは、本文の下敷きを不透明にする必要があります。',
        wallpaperChoose: '画像を選ぶ',
        wallpaperUploading: 'アップロード中…',
        wallpaperNone: '壁紙はありません。',
        wallpaperCurrent: '現在の壁紙',
        wallpaperShape: '大きさ',
        wallpaperMeta: '{width} × {height} · {kb} KB · {format}',
        wallpaperRemove: '壁紙をやめる',
        wallpaperPending: '壁紙はまだ反映されていません。下の保存を押してください。',
        preview: 'プレビュー',
        previewHint: 'いくつかの面のおおよその見え方で、判定ではありません。効くのは保存したときにサーバーが出す十八組の数値と、各項目の下に出るメッセージです。',
        previewSubstituted: 'これらはまだ色になっていないので、プレビューでは組み込みの値で代用しています：{tokens}',
        previewHeader: 'カテゴリの例',
        previewClip: 'ボイスの例',
        previewResting: '通常',
        previewHover: 'ホバー',
        previewBody: '本文はこの下敷きの上に乗ります。',
        previewFooter: 'フッターの文字とリンク',
        save: '保存して有効にする',
        saveHint: '配色はまとめて送ります。下に並ぶ問題で拒否された場合は何も変わりません。行も書かれず、theme.css にも触れません。',
        saved: '保存しました。theme.css を書き直しています。サイト側は一度読み込み直すと反映されます。',
        revert: '組み込みの配色に戻す',
        revertHint: 'いまのテーマを無効にし、theme.css を空にします。行自体は残るので、いつ有効だったかの記録は消えません。',
        reverted: '組み込みの配色に戻しました。上の入力はそのまま残っているので、もう一度保存すれば戻せます。',
        refused: 'このテーマは拒否されました。保存もされず、theme.css にも触れていません：',
        problem: {
          unknown_token: '{token} はサイトが持っている色の名前ではないため、そのまま通さずに拒否します。通してしまうと、このフォームからスタイルシートへ任意の CSS を書けることになります。',
          missing_token: 'この項目が送られていません。',
          not_a_colour: '色ではありません。#rgb、#rrggbb、#rrggbbaa のいずれかで書いてください。',
          translucent: '半透明の色にはコントラスト比が一つに定まりません。最終的な見え方が後ろにあるもの次第だからです。半透明にできるのは本文の下敷きだけで、それも壁紙がないときに限ります。',
          contrast: 'コントラストが足りません：',
          content_backing_translucent: '背景に壁紙があるときは、本文の下敷きを不透明にしてください。そうしないと、本文が読めるかどうかがその部分の絵柄次第になります。',
          name_empty: '名前を付けてください。',
          name_too_long: '{max} 文字を超えています。',
          not_an_object: '送られてきたテーマをサーバーが読み取れませんでした。',
          wallpaper_missing: 'この壁紙はサーバー上にもうありません。もう一度アップロードするか、壁紙を外して保存してください。（バックアップから復元した直後に起こりやすい状況です。テーマの記録は戻っても、画像は戻らないためです。）',
          wallpaper_malformed: '壁紙のファイル名として受け付けられない形式です。手で入力せず、上のアップロードから選んでください。',
          unknown: '拒否されました：{code}'
        }
      }
    }
  }
}
